import React from "react";
import { Form } from "react-bootstrap";
import { Formik } from "formik";
import Alert from "components/Alert";
import Button from "components/Button";
import InfoTooltip from "components/InfoTooltip";

import "./IndividualEnroll.scss";

import {
  emailValidator,
  noWhitespaceRegex,
  testUsername,
  whiteSpaceError,
} from "utils/fieldValidators";
import * as yup from "yup";
import { ScrollToFieldError } from "utils/form";
import { invitationCodeParser } from "../../../utils/fieldValidators";

interface SignupFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  // invitationCode comes from this.props.invitationCode (optional) spread into
  // initialValues, so it may be undefined.
  invitationCode?: string;
}

interface SubmittedUser {
  username: string;
  email: string;
  password: string;
  role: string;
  invitationCode?: string;
  inviteId?: string;
}

interface Props {
  error?: string;
  isFetching?: boolean;
  onSubmit?: (user: SubmittedUser) => void;
  invitationCode?: string;
  inviteId?: string;
  showInvitationCodeForm?: boolean;
  showTerms?: boolean;
  initialValues?: Partial<SignupFormValues>;
}

const schema = yup.object({
  username: yup
    .string()
    .label("Username")
    .required()
    .min(3, "Must be at least three characters")
    .max(25)
    .matches(noWhitespaceRegex, whiteSpaceError)
    // testUsername's `this` type ({ createError }) is disjoint from yup's
    // TestFunction signature; boundary-cast so its runtime behavior is kept.
    .test(
      "username",
      "Invalid username.",
      testUsername as unknown as yup.TestFunction<string | undefined>
    ),
  email: emailValidator("You must enter a valid email address")
    .label("Email")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .max(80)
    .required(),
  password: yup
    .string()
    .label("Password")
    .required()
    .min(8, "Seems a bit short...")
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
      "Must be at least 8 characters long with one uppercase, one lowercase, one number and one special character(e.g. #?!@$%^&*- )"
    ),
  confirmPassword: yup
    .string()
    .required()
    .label("Confirm password")
    .test("passwords-match", "Passwords must match", function (value) {
      return this.parent.password === value;
    }),
  invitationCode: yup.string().label("Invitation Code").notRequired(),
});

class IndividualSignupForm extends React.Component<Props> {
  static defaultProps = {
    showInvitationCodeForm: true,
    showTerms: true,
  };

  submitUser = (values: SignupFormValues) => {
    let user: SubmittedUser = {
      username: values.username,
      email: values.email,
      password: values.password,
      role: "Individual",
      invitationCode: values.invitationCode,
      inviteId: this.props.inviteId,
    };

    this.props.onSubmit!(user);
  };

  render() {
    var articleStyle = {
      paddingBottom: 0,
    };

    const disablePlanID = !!this.props.invitationCode;

    return (
      <div>
        <Formik
          validateOnChange={false}
          validationSchema={schema}
          enableReinitialize={true}
          onSubmit={(values) => {
            this.submitUser(values);
          }}
          initialValues={{
            email: "",
            password: "",
            confirmPassword: "",
            username: "",
            invitationCode: this.props.invitationCode,
            ...this.props.initialValues,
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
            setFieldValue,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <div className="mega-container">
                <div
                  className="step-container is-active"
                  data-circle-percent="10"
                >
                  <div id="form-employer-profile">
                    <section style={{ padding: 0 }}>
                      <article style={articleStyle}>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicUsername"
                        >
                          <Form.Label>Username</Form.Label>
                          <Form.Control
                            name="username"
                            value={values.username}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.username && !!errors.username}
                            isValid={touched.username && !errors.username}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.username}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicEmail">
                          <Form.Label>Personal Email Address</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.email && !!errors.email}
                            isValid={touched.email && !errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                          className="mb-3"
                          controlId="formBasicPassword"
                        >
                          <Form.Label>
                            Password{" "}
                            <InfoTooltip
                              iconSize={16}
                              tooltipBody="Your password must be at least 8 characters long with one uppercase, one lowercase, one number and one special character(e.g. #?!@$%^&*- )"
                            />
                          </Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.password && !!errors.password}
                            isValid={touched.password && !errors.password}
                          />
                          <Form.Control.Feedback className="valid-account-creation">
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicPasswordConfirm"
                        >
                          <Form.Label>Confirm Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="confirmPassword"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.confirmPassword &&
                              !!errors.confirmPassword
                            }
                            isValid={
                              touched.confirmPassword && !errors.confirmPassword
                            }
                          />
                          <Form.Control.Feedback className="valid-account-creation">
                            Passwords match!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.confirmPassword}
                          </Form.Control.Feedback>
                        </Form.Group>

                        {this.props.showInvitationCodeForm && (
                          <Form.Group
                            className="mb-3"
                            controlId="formBasicInvitationCode"
                          >
                            <Form.Control
                              disabled={disablePlanID}
                              name="invitationCode"
                              value={values.invitationCode}
                              placeholder="Plan ID"
                              onBlur={handleBlur}
                              onChange={(event) => {
                                const value = invitationCodeParser(
                                  event,
                                  // PRESERVED BUG (conversion-only): invitationCodeParser
                                  // takes a single arg; this second arg is silently ignored
                                  // at runtime. Kept verbatim to avoid changing behavior.
                                  // @ts-expect-error excess argument preserved as-is
                                  values.invitationCode
                                );
                                void setFieldValue("invitationCode", value);
                              }}
                              isInvalid={
                                touched.invitationCode &&
                                !!errors.invitationCode
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.invitationCode}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}
                      </article>
                      {this.props.error && (
                        <Alert type="error" msg={this.props.error} />
                      )}
                      <Button
                        name="submit"
                        color="create-account"
                        btnLabel="Create Icon Account"
                        loading={this.props.isFetching}
                      />
                    </section>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default IndividualSignupForm;
